<template>
    <v-container fluid>
      <loading-overlay :loading="loading" />
      <div style="width: 100%;">
        <v-tabs v-model="selectedIndex">
          <v-tab-item v-for="(item, i) in tabs" :key="i">
            <div class="scroll-container">
              <custom-toolbar
                :logo="getLogoUrl(item)"
                :search="search"
                :selectedColumn="selectedColumn"
                :columnOptions="columnOptions"
                :items="items"
                :categories="categories"
                :providers="providers"
                :service_type="service_type"
                :exportToExcel="() => exportToExcel(item)"
                showSelect
                showSearch
                @update:search="search = $event"
                @update:selectedColumn="selectedColumn = $event"
              />
              <div class="row-count">
                <template>
                  Total Items: <b>{{ rowCountMaster.total }}</b> 
                  | KOCOWA Items: <b>{{ rowCountMaster.kocowa }}</b>
                  | Non-KOCOWA Items: <b>{{ rowCountMaster.nonKocowa }}</b>
                  | Total Kocowa Duration: <b>{{ totalKocowaDuration }}</b> hours
                </template>
              </div>
              <div ref="dummyScroll" id="scroll" style="overflow-x: auto !important;" @scroll="syncScrolls">
                <div id="dummyTable" ref="dummyTable" style="height:1px;"></div>
              </div>
              <v-data-table
                ref="dataTable"
                :headers="headers[item]"
                :items="filteredItemsByTab[item]"
                class="elevation-1 wide-table data-table"
                :items-per-page="itemsPerPage"
                :hide-default-footer="true"
                :key="tableKey"
                :sort-by="'season_on_air_date'"
                :sort-desc="true"
                fixed-header
                height="1200px"
              />
            </div>
          </v-tab-item>
        </v-tabs>
      </div>
    </v-container>
  </template>
  
  <script>
  import { mapGetters, mapActions } from 'vuex';
  import axios from 'axios';
  import * as XLSX from 'xlsx';
  import LoadingOverlay from './LoadingOverlay.vue';
  import CustomToolbar from './CustomToolbar.vue';
  import MasterLogo from '@/assets/master_logo.png';
  
  
  const HEADERS = {
    MASTER: [
      { text: 'Season ID', value: 'season_id' },
      { text: 'Provider', value: 'provider_name' },
      { text: 'English Title', value: 'season_name' },
      { text: 'Korean Title', value: 'season_name_kr' },
      { text: 'Publish', value: 'season_on_air_date', width: '110px' },
      { text: 'Premiere', value: 'premiere', width: '110px' },
      { text: 'Finale', value: 'last_episode_on_air_date', width: '110px' },
      { text: '# of Episodes', value: 'num_of_episode' },
      { text: '# of pip active', value: 'num_of_pip_active' },
      { text: 'Category', value: 'category_name' },
      { text: 'Media Duration (hour)', value: 'media_duration' },
      { text: 'Service Type', value: 'service_type' },
      { text: 'Deploy Groups', value: 'deploy_groups' },
      { text: 'Deploy Subtitles', value: 'deploy_sub' },
      { text: 'Service Package', value: 'service_package' },
      { text: 'Subtitles Languages', value: 'season_subtitle' }
    ]
  };
  
  export default {
    name: 'MasterPage',
    components: {
        LoadingOverlay,
        CustomToolbar
    },
    data() {
      return {
        tabs: ['MASTER'],
        items: [],
        search: '',
        selectedColumn: 'season_id',
        columnOptions: HEADERS.MASTER.map(header => ({ text: header.text, value: header.value })),
        itemsPerPage: 5000,
        loading: true,
        tableKey: 0,
        selectedTab: 'MASTER',
        selectedIndex: 0,
        categories: [],
        providers: [],
        service_types: [],
      };
    },
    computed: {
      ...mapGetters(['userEmail', 'token', 'auth']),
      headers() {
        return HEADERS;
      },
      rowCount() {
        return (this.filteredItemsByTab[this.selectedTab] || []).length.toLocaleString();
      },
      rowCountMaster() {
        const totalItems = this.filteredItemsByTab[this.selectedTab].length;
        const kocowaItems = this.filteredItemsByTab[this.selectedTab].filter(item => item.service_type === 'KOCOWA').length;
        const nonKocowaItems = totalItems - kocowaItems;
  
        return {
          total: totalItems.toLocaleString(),
          kocowa: kocowaItems.toLocaleString(),
          nonKocowa: nonKocowaItems.toLocaleString()
        };
      },
      totalMediaDuration() {
        const total = this.filteredItemsByTab[this.selectedTab].reduce((total, item) => {
          return total + (parseFloat(item.media_duration) || 0);
        }, 0);
  
        return parseFloat(total.toFixed(2)).toLocaleString();
      },
      totalKocowaDuration() {
        const total = this.filteredItemsByTab[this.selectedTab]
          .filter(item => item.service_type === 'KOCOWA')
          .reduce((total, item) => {
            return total + (parseFloat(item.media_duration) || 0); 
          }, 0);
  
        return parseFloat(total.toFixed(2)).toLocaleString();
      },
      filteredItems() {
        const search = this.search.toLowerCase();
        return this.items
          .filter(item =>
            String(item[this.selectedColumn] || '').toLowerCase().includes(search)
          )
          .map(item => ({
            ...item,
            service_type: item.service_type === 'kocowa' 
              ? 'KOCOWA' 
              : (item.service_type === 'none' ? 'Non KOCOWA' : item.service_type)
          }));
      },
      filteredItemsByTab() {
        const filterMap = {
          MASTER: () => this.filteredItems
        };
  
        return this.tabs.reduce((result, tab) => {
          result[tab] = filterMap[tab]();
          return result;
        }, {});
      }
    },
    methods: {
      ...mapActions(['fetchUserData']),
      async fetchItems() {
        try {
          this.loading = true;
          if (!this.userEmail || !this.token) {
            console.error('User email or token is missing');
            this.loading = false;
            return;
          }
  
          const url = `https://pcld-api.wavveanalytics.com/API/contents-list?user_email=${encodeURIComponent(this.userEmail)}&token=${encodeURIComponent(this.token)}`;
          
          const response = await axios.get(url);
          
          if (this.auth === 'Operator') {
            this.items = response.data.content_list || [];
            this.categories = [...new Set(this.items.map(item => item.category_name).filter(Boolean))];
            this.providers = [...new Set(this.items.map(item => item.provider_name).filter(Boolean))];
            this.service_type = [...new Set(this.items.map(item => item.service_type).filter(Boolean))];
          }
        } catch (error) {
          console.error('Error fetching items:', error);
          this.items = [];
        } finally {
          this.loading = false;
        }
      },
      getLogoUrl(item) {
        const logos = {
          'MASTER': MasterLogo
        };
        return logos[item] || '@/assets/master_logo.png';
      },
      syncScrolls(event) {
        const dataTableWrapper = document.querySelector('.v-data-table__wrapper');
        const dummyScroll = this.$refs.dummyScroll;
        const dummyTable = this.$refs.dummyTable;
        const dataTable = this.$refs.dataTable;
  
        if (dummyTable && dataTable) {
          if (dataTableWrapper) {
            // Scroll synchronization logic
            if (event.target === dataTableWrapper) {
              dummyScroll[0].scrollLeft = dataTableWrapper.scrollLeft;
            } else if (event.target === dummyScroll[0]) {
              dataTableWrapper.scrollLeft = dummyScroll[0].scrollLeft;
            }
          } else {
            console.error('Data table wrapper is undefined');
          }
        } else {
          console.error('One or both elements are undefined');
        }
      },
  
      syncScrollWidth() {
        const dataTableWrapper = document.querySelector('.v-data-table__wrapper > table');
        const dummyTable = document.getElementById('dummyTable');
  
        if (dataTableWrapper && dummyTable) {
          dummyTable.style.width = dataTableWrapper.offsetWidth + "px";
        } else {
          console.error('dataTableWrapper or dummyTable is undefined');
        }
      },
      handleTabChange() {
        this.handleSearch();
        this.$nextTick(() => {
          this.$nextTick(() => {
            // Data table is fully rendered
            this.syncScrollWidth(); // Set the width of dummyTable
            window.scrollTo(0, 0);
          });
        });
      },
      handleSearch() {
        this.tableKey += 1;
      },
      exportToExcel(tab) {
        const headers = HEADERS[tab].map(header => header.text);
  
        const data = this.filteredItemsByTab[tab].map(item => {
          return HEADERS[tab].reduce((acc, header) => {
            acc[header.text] = item[header.value]; // 헤더의 text와 value를 매핑
            return acc;
          }, {});
        });
  
        const ws = XLSX.utils.json_to_sheet(data, { header: headers });
  
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `${tab}Data`);
  
        XLSX.writeFile(wb, `Data_${tab}.xlsx`);
      }
    },
    mounted() {
      window.addEventListener('resize', this.syncScrollWidth);
      this.$nextTick(() => {
        this.syncScrollWidth();
      });
    },
  
    updated() {
      this.$nextTick(() => {
        this.syncScrollWidth(); // Adjust dummyTable width after data update
      });
    },
  
    beforeUnmount() {
      const dummyScroll = this.$refs.dummyScroll;
      const dataTable = this.$refs.dataTable;
  
      if (dummyScroll && dataTable) {
        const dataTableWrapper = dataTable.$el?.querySelector('.v-data-table__wrapper');
        if (dataTableWrapper) {
          dummyScroll.removeEventListener('scroll', this.syncScrolls);
          dataTableWrapper.removeEventListener('scroll', this.syncScrolls);
        }
      }
      window.removeEventListener('resize', this.syncScrollWidth); // Clean up event listener
    },
  
    watch: {
      selectedIndex() {
        this.selectedTab = this.tabs[this.selectedIndex];
        this.search = '';
        this.columnOptions = HEADERS[this.selectedTab].map(header => ({ text: header.text, value: header.value }));
  
        this.$nextTick(() => {
          this.handleTabChange();
        });
      }
    },
  
    created() {
      this.fetchItems();
    }
  };
  
  </script>
  
  <style scoped>
  
  .scroll-container {
    position: relative;
    margin: 15px;
  }
  
  .v-data-table__wrapper {
    overflow-x: auto !important;
  }
  
  .wide-table {
    width: 100% !important; /* Ensure the table takes full width */
    overflow-x: hidden !important; /* Remove horizontal scroll */
  }
  
  .toolbar-container {
    margin-bottom: 60px;
    padding-top: 35px;
  }
  
  ::v-deep .v-data-table-header th {
    background-color: #ededed !important;
    white-space: nowrap; /* Prevent header text wrapping */
    color: #636363 !important;
  }
  
  ::v-deep .v-data-table-header th:hover {
    color: black !important;
  }
  
  ::v-deep .v-data-table-header th > .v-data-table-header__content {
    color: white;
  }
  
  ::v-deep .v-data-table tbody tr:hover {
    background-color: #e5eef9 !important;
  }
  
  .row-count {
    padding: 7px 0;
    font-size: 12px;
    color: #636363;
  }
  
  .v-data-table {
    table-layout: auto !important;
    width: 100% !important;
  }
  
  ::v-deep .v-data-table > table {
    width: auto !important;
  }
  
  ::v-deep .v-data-table > table > thead > tr > th {
    white-space: nowrap; /* Prevent header text wrapping */
  }
  
  ::v-deep .v-data-table > table > tbody > tr > td {
    white-space: nowrap; /* Prevent cell text wrapping */
  }
  
  ::v-deep .v-data-table > div {
    display: block; /* Set block element to occupy full width */
    width: 100% !important; /* Set block element width to 100% */
  }
  
  .v-data-table>.v-data-table__wrapper>table {
      border-spacing: 0;
  }
  
  .container {
    padding:20px;
  }
  </style>
  